import moment from 'moment';
import {
  DashboardDateRange,
  DashboardPeriod,
  DashboardPeriodRequest,
  DashboardScope,
  DashboardWireScope,
  IsoDate,
} from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';
import {
  InactivityPanelVariant,
  InactivityPanelVm,
  WaitBadgeTier,
} from '@partner/feature-modules/dashboard/domain/model/executive-snapshot.model';
import { formatCompactCurrency } from './compact-currency.helper';
import { formatDateRange } from './dashboard-filters.helper';

/** Half-open wire range: `from` inclusive, `to` exclusive. */
export type WireDateRange = Pick<DashboardPeriodRequest, 'from' | 'to'>;

const ISO_DATE_FORMAT = 'YYYY-MM-DD';
const DAY_MS = 24 * 60 * 60 * 1000;
const WAIT_BADGE_WARNING_DAYS = 7;
const WAIT_BADGE_DANGER_DAYS = 14;
const LAST_YEAR_LABEL = "last year's";
const WHOLE_DOLLAR_DIGITS = '1.0-0';
const CENTS_DIGITS = '1.2-2';

export const INACTIVITY_PANEL_MIN_DAYS = 30;
export const INACTIVITY_DEACTIVATION_DAYS = 90;

function parseIsoDate(value: IsoDate | null | undefined): moment.Moment | null {
  if (typeof value !== 'string') {
    return null;
  }
  const parsed = moment(value, ISO_DATE_FORMAT, true);
  return parsed.isValid() ? parsed : null;
}

/** Bar range (inclusive `to`) → wire range (`to` + 1 calendar day); null for a null or malformed range. */
export function toWireRange(range: DashboardDateRange | null): WireDateRange | null {
  const from = parseIsoDate(range?.from);
  const to = parseIsoDate(range?.to);
  if (!from || !to) {
    return null;
  }
  return { from: from.format(ISO_DATE_FORMAT), to: to.add(1, 'day').format(ISO_DATE_FORMAT) };
}

export function toWireScope(scope: DashboardScope): DashboardWireScope {
  return scope === DashboardScope.WHOLE_TEAM ? DashboardWireScope.TEAM : DashboardWireScope.MY;
}

/** Null when the range is missing (an incomplete custom pair). */
export function toDashboardPeriodRequest(
  range: DashboardDateRange | null,
  scope: DashboardScope,
): DashboardPeriodRequest | null {
  const wireRange = toWireRange(range);
  return wireRange ? { ...wireRange, scope: toWireScope(scope) } : null;
}

/** The snapshot's own name for the shared builder; kept so its callers and specs stay untouched. */
export const toExecutiveSnapshotRequest = toDashboardPeriodRequest;

/** "month to date" / "quarter to date" / "year to date"; CUSTOM → `Jul 1 – Jul 27, 2026` (empty without a range). */
export function periodTitle(period: DashboardPeriod, range: DashboardDateRange | null): string {
  switch (period) {
    case DashboardPeriod.MTD:
      return 'month to date';
    case DashboardPeriod.QTD:
      return 'quarter to date';
    case DashboardPeriod.YTD:
      return 'year to date';
    default:
      return formatDateRange(range) ?? '';
  }
}

/** Tile label following the selected period: "Funded (MTD)" … "Funded (custom range)". */
export function fundedTileLabel(period: DashboardPeriod): string {
  return period === DashboardPeriod.CUSTOM ? 'Funded (custom range)' : `Funded (${period})`;
}

/**
 * `digitsInfo` for the `currency` pipe: whole amounts render as `$185,000` (Figma shows full dollars),
 * fractional ones always with both decimals (`$185,000.50`) — never a single-decimal money string.
 */
export function currencyDigits(amount: number): string {
  return Number.isInteger(amount) ? WHOLE_DOLLAR_DIGITS : CENTS_DIGITS;
}

/** Red from 14 days, orange from 7, grey below. */
export function waitBadgeTier(days: number): WaitBadgeTier {
  if (days >= WAIT_BADGE_DANGER_DAYS) {
    return WaitBadgeTier.DANGER;
  }
  return days >= WAIT_BADGE_WARNING_DAYS ? WaitBadgeTier.WARNING : WaitBadgeTier.NEUTRAL;
}

/** Whole days from `date` to `now`, never negative (the server clock may run ahead of the browser). */
export function daysBetween(date: Date, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - date.getTime()) / DAY_MS));
}

/** Progress toward deactivation as a percentage: 30 → 33.3, 90 → 100, 120 → 100. */
export function inactivityProgress(days: number): number {
  const clamped = Math.min(Math.max(days, 0), INACTIVITY_DEACTIVATION_DAYS);
  return (clamped / INACTIVITY_DEACTIVATION_DAYS) * 100;
}

/**
 * Null days (no qualifying submission yet) → the empty variant; 0–29 days → no panel (null);
 * 30 days and more → the warning variant with the bar toward 90 days.
 */
export function buildInactivityPanelVm(days: number | null | undefined): InactivityPanelVm | null {
  if (days === null || days === undefined) {
    return { variant: InactivityPanelVariant.EMPTY, days: null, progressPercent: 0 };
  }
  if (days < INACTIVITY_PANEL_MIN_DAYS) {
    return null;
  }
  return { variant: InactivityPanelVariant.WARNING, days, progressPercent: inactivityProgress(days) };
}

/** `2025` when the bar's `from` and inclusive `to` share a year; otherwise "last year's" (a custom range can span two). */
export function priorYearLabel(range: DashboardDateRange | null): string {
  const from = parseIsoDate(range?.from);
  const to = parseIsoDate(range?.to);
  if (!from || !to || from.year() !== to.year()) {
    return LAST_YEAR_LABEL;
  }
  return String(from.year() - 1);
}

/** "Ahead of 2025 pace by $610K funded" / "Behind 2025 pace by $X funded" / "On 2025 pace". */
export function paceLine(amount: number, priorYearAmount: number, range: DashboardDateRange | null): string {
  const year = priorYearLabel(range);
  const delta = amount - priorYearAmount;
  if (delta === 0) {
    return `On ${year} pace`;
  }
  const direction = delta > 0 ? 'Ahead of' : 'Behind';
  return `${direction} ${year} pace by ${formatCompactCurrency(Math.abs(delta))} funded`;
}
